import { Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useResource } from "../hooks/useResource";
import { EmptyState, ErrorBanner, LoadingState, PageHeader } from "../components/common";
import type { PortalSummary } from "./types";

/**
 * The account at a glance: what arrived, what is unanswered, what it was worth.
 *
 * `awaitingFeedback` is the number worth staring at. Outcomes are how the trial
 * gets judged, and they only exist if somebody reports them — so the count of
 * leads nobody has answered for is a link straight into that filtered list, not
 * just a statistic.
 */

const MONEY = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

export function PortalOverviewPage() {
  const summary = useResource<PortalSummary>("/api/portal/summary");

  if (summary.loading && !summary.data) return <LoadingState label="Loading your account…" />;
  if (summary.error) return <ErrorBanner error={summary.error} onRetry={summary.reload} />;
  if (!summary.data) return null;

  const { totals, byOutcome } = summary.data;
  const reported = byOutcome
    .filter((row) => row.outcome !== "delivered")
    .sort((a, b) => b.count - a.count);

  return (
    <Box>
      <PageHeader
        title="Overview"
        subtitle="Everything routed to your account, and what you have told us about it."
        actions={
          <Button startIcon={<RefreshIcon />} onClick={summary.reload}>
            Refresh
          </Button>
        }
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 2,
          mb: 2,
        }}
      >
        <Stat label="Leads received" value={String(totals.total)} hint="Since the account opened" />
        <Stat label="Last 7 days" value={String(totals.last7Days)} hint="Newly routed to you" />
        <Stat
          label="Awaiting your feedback"
          value={String(totals.awaitingFeedback)}
          hint="Leads with no outcome reported"
          to={totals.awaitingFeedback > 0 ? "/portal/leads?awaiting=true" : undefined}
          emphasis={totals.awaitingFeedback > 0}
        />
        <Stat label="Won" value={String(totals.won)} hint="Jobs you booked" />
        <Stat
          label="Value won"
          value={MONEY.format(totals.valueUsd)}
          hint="Summed from the values you reported"
        />
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="overline" color="text.secondary">
          Reported outcomes
        </Typography>
        {reported.length === 0 ? (
          <EmptyState
            title="Nothing reported yet"
            hint="Open a lead and say what happened. It takes a click, and it is the only way this account's results can be measured."
          />
        ) : (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            {reported.map((row) => (
              <Chip
                key={row.outcome}
                label={`${row.outcome} · ${row.count}`}
                variant="outlined"
                color={row.outcome === "won" ? "success" : row.outcome === "bad_lead" ? "error" : "default"}
                component={RouterLink}
                to={`/portal/leads?outcome=${encodeURIComponent(row.outcome)}`}
                clickable
              />
            ))}
          </Stack>
        )}
      </Paper>
    </Box>
  );
}

function Stat({
  label,
  value,
  hint,
  to,
  emphasis,
}: {
  label: string;
  value: string;
  hint: string;
  /** Present when the number is worth acting on, which makes the card a link. */
  to?: string;
  emphasis?: boolean;
}) {
  const card = (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        height: "100%",
        borderColor: emphasis ? "warning.main" : undefined,
        ...(to ? { "&:hover": { bgcolor: "action.hover" } } : {}),
      }}
    >
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    </Paper>
  );

  // Wrapped rather than made polymorphic: `<Paper component={RouterLink}>` works,
  // but only on the branch where `to` exists, and the conditional spread that
  // would express that reads worse than two lines.
  if (!to) return card;
  return (
    <Box component={RouterLink} to={to} sx={{ display: "block", textDecoration: "none", color: "inherit" }}>
      {card}
    </Box>
  );
}
